import React, { useState, useEffect, useRef } from 'react';
import './InlinePersonalitySliders.css';

interface SliderData {
  id: string;
  label: string;
  value: number;
  min: number;
  max: number;
}

interface InlinePersonalitySlidersProps {
  sliders: SliderData[];
  onSubmit: (values: Record<string, number>) => void;
}

const InlinePersonalitySliders: React.FC<InlinePersonalitySlidersProps> = ({
  sliders,
  onSubmit
}) => {
  // Convert 0-100 scale from backend to 1-5 scale for display
  const convertFromBackendScale = (value: number): number => {
    // Map 0-100 to 1-5: 0→1, 1-24→1, 25-49→2, 50-74→3, 75-99→4, 100→5
    if (value <= 0) return 1;
    if (value <= 24) return 1;
    if (value <= 49) return 2;
    if (value <= 74) return 3;
    if (value <= 99) return 4;
    return 5;
  };

  // Track if we've initialized to prevent re-initialization
  const initializedRef = useRef(false);

  // Initialize values from slider defaults with null safety
  const [values, setValues] = useState<Record<string, number>>(() => {
    if (initializedRef.current) {
      // If already initialized, return current values (this shouldn't happen, but safety check)
      return {};
    }
    
    const initialValues: Record<string, number> = {};
    sliders.forEach(slider => {
      let initialValue = slider.value ?? 3; // Fallback to 3 (middle of 1-5 scale) if value is null/undefined
      
      // If value is in 0-100 range (backend scale), convert to 1-5
      if (initialValue > 5) {
        initialValue = convertFromBackendScale(initialValue);
      }
      
      // Clamp to valid 1-5 range and ensure it matches slider min/max
      initialValue = Math.max(slider.min || 1, Math.min(slider.max || 5, initialValue));
      initialValues[slider.id] = initialValue;
    });
    
    initializedRef.current = true;
    console.log('🎛️ SLIDERS: Initialized with values:', initialValues);
    return initialValues;
  });

  const [isSubmitting, setIsSubmitting] = useState(false);

  // Track previous sliders to avoid unnecessary updates
  const prevSlidersRef = useRef<SliderData[]>([]);
  
  // Sync with sliders prop changes (only if values don't exist for a slider)
  useEffect(() => {
    // Check if sliders actually changed (by comparing IDs and values)
    const slidersChanged = 
      prevSlidersRef.current.length !== sliders.length ||
      sliders.some((slider, idx) => {
        const prev = prevSlidersRef.current[idx];
        return !prev || prev.id !== slider.id || prev.value !== slider.value;
      });
    
    if (!slidersChanged) {
      return; // No actual change, skip update
    }
    
    prevSlidersRef.current = sliders;
    
    // Only update if we have new sliders that aren't in our state
    setValues(prev => {
      const hasNewSliders = sliders.some(slider => prev[slider.id] === undefined);
      if (!hasNewSliders) {
        return prev; // No new sliders, return previous state
      }
      
      const updated = { ...prev };
      sliders.forEach(slider => {
        if (updated[slider.id] === undefined) {
          let initialValue = slider.value ?? 3;
          if (initialValue > 5) {
            initialValue = convertFromBackendScale(initialValue);
          }
          updated[slider.id] = Math.max(slider.min || 1, Math.min(slider.max || 5, initialValue));
        }
      });
      return updated;
    });
  }, [sliders]);

  // 🎛️ SUBMIT HANDLER: Removed duplicate - using the one defined below

  // Snap to discrete 1-5 values (5 positions only)
  const snapToDiscreteValue = (percentage: number, min: number, max: number): number => {
    // Clamp percentage to valid range [0, 1]
    const clampedPercentage = Math.max(0, Math.min(1, percentage));
    
    // For 5 discrete values (1-5), divide track into 5 equal segments
    // Segment boundaries: [0, 0.2), [0.2, 0.4), [0.4, 0.6), [0.6, 0.8), [0.8, 1.0]
    // Map to values: 1, 2, 3, 4, 5
    const numValues = max - min + 1; // 5
    const segmentSize = 1 / numValues; // 0.2
    
    // Calculate which segment (0-4) the percentage falls into
    // Use Math.floor to map each segment to its value
    let segmentIndex = Math.floor(clampedPercentage / segmentSize);
    
    // Handle edge case: 100% should map to the last value
    if (clampedPercentage >= 1.0) {
      segmentIndex = numValues - 1;
    }
    
    // Clamp to valid range (0-4)
    segmentIndex = Math.max(0, Math.min(numValues - 1, segmentIndex));
    
    // Convert segment index (0-4) to actual value (1-5)
    const value = min + segmentIndex;
    
    // Final safety clamp to ensure value is within min-max range
    return Math.max(min, Math.min(max, value));
  };

  const handleSliderChange = (sliderId: string, newValue: number) => {
    console.log(`🎛️ SLIDER: Changing ${sliderId} to ${newValue}`);
    setValues(prev => ({
      ...prev,
      [sliderId]: newValue
    }));
  };

  const handleTrackClick = (e: React.MouseEvent<HTMLDivElement>, slider: SliderData) => {
    // Don't handle track click if the click originated from the thumb or its children
    const target = e.target as HTMLElement;
    if (target.classList.contains('slider-thumb') || target.closest('.slider-thumb')) {
      return;
    }
    
    console.log('🎛️ SLIDER: Track clicked for', slider.id);
    const rect = e.currentTarget.getBoundingClientRect();
    const x = e.clientX - rect.left;
    const newPercentage = Math.max(0, Math.min(1, x / rect.width));
    const newValue = snapToDiscreteValue(newPercentage, slider.min, slider.max);
    console.log('🎛️ SLIDER: New value calculated (snapped to 1-5):', newValue);
    handleSliderChange(slider.id, newValue);
  };

  const handleMouseDown = (e: React.MouseEvent<HTMLDivElement>, slider: SliderData) => {
    console.log('🎛️ SLIDER: Mouse down on thumb for', slider.id);
    e.preventDefault();
    e.stopPropagation(); // Prevent track click from firing
    
    const trackElement = e.currentTarget.parentElement!;
    const startX = e.clientX;
    let hasMoved = false;
    
    const updateValue = (clientX: number) => {
      // Recalculate trackRect on each move to handle re-renders
      const trackRect = trackElement.getBoundingClientRect();
      const x = clientX - trackRect.left;
      const newPercentage = Math.max(0, Math.min(1, x / trackRect.width));
      const newValue = snapToDiscreteValue(newPercentage, slider.min, slider.max);
      handleSliderChange(slider.id, newValue);
    };

    const handleMouseMove = (moveEvent: MouseEvent) => {
      moveEvent.preventDefault(); // Prevent default to avoid conflicts
      moveEvent.stopPropagation(); // Prevent any other handlers
      hasMoved = true;
      updateValue(moveEvent.clientX);
    };

    const handleMouseUp = (upEvent: MouseEvent) => {
      upEvent.preventDefault();
      upEvent.stopPropagation(); // Prevent track click from firing after mouse up
      document.removeEventListener('mousemove', handleMouseMove);
      document.removeEventListener('mouseup', handleMouseUp);
      console.log('🎛️ SLIDER: Mouse drag complete for', slider.id, hasMoved ? '(dragged)' : '(clicked)');
    };

    document.addEventListener('mousemove', handleMouseMove);
    document.addEventListener('mouseup', handleMouseUp);
  };

  const handleTouchStart = (e: React.TouchEvent<HTMLDivElement>, slider: SliderData) => {
    e.preventDefault();
    e.stopPropagation();
    
    const trackElement = e.currentTarget.parentElement!;
    
    const updateValue = (clientX: number) => {
      // Recalculate trackRect on each move to handle re-renders
      const trackRect = trackElement.getBoundingClientRect();
      const x = clientX - trackRect.left;
      const newPercentage = Math.max(0, Math.min(1, x / trackRect.width));
      const newValue = snapToDiscreteValue(newPercentage, slider.min, slider.max);
      handleSliderChange(slider.id, newValue);
    };

    const handleTouchMove = (moveEvent: TouchEvent) => {
      moveEvent.preventDefault(); // Prevent default scrolling
      if (moveEvent.touches.length > 0) {
        updateValue(moveEvent.touches[0].clientX);
      }
    };

    const handleTouchEnd = () => {
      document.removeEventListener('touchmove', handleTouchMove);
      document.removeEventListener('touchend', handleTouchEnd);
      console.log('🎛️ SLIDER: Touch drag complete for', slider.id);
    };

    document.addEventListener('touchmove', handleTouchMove, { passive: false });
    document.addEventListener('touchend', handleTouchEnd);
  };

  // Convert 1-5 scale to 0-100 scale for backend compatibility
  const convertToBackendScale = (value: number): number => {
    // Map 1-5 to 0-100: 1→0, 2→25, 3→50, 4→75, 5→100
    const mapping: Record<number, number> = {
      1: 0,
      2: 25,
      3: 50,
      4: 75,
      5: 100
    };
    return mapping[value] ?? 50; // Default to 50 if value is out of range
  };

  // Handle submit
  const handleSubmit = async () => {
    if (isSubmitting) return;
    
    setIsSubmitting(true);
    try {
      // Convert 1-5 scale values to 0-100 scale for backend
      const backendValues: Record<string, number> = {};
      Object.entries(values).forEach(([key, value]) => {
        backendValues[key] = convertToBackendScale(value);
      });
      
      console.log('🎛️ SLIDERS: Submitting values (1-5 scale):', values);
      console.log('🎛️ SLIDERS: Converted to backend scale (0-100):', backendValues);
      await onSubmit(backendValues);
    } catch (error) {
      console.error('🎛️ SLIDERS: Submit error:', error);
      setIsSubmitting(false);
    }
  };

  // Helper functions for styling and content
  const getSliderIcon = (sliderId: string): React.JSX.Element => {
    console.log('🎨 SLIDER ICON v4.0: Using new SVG assets for', sliderId);
    
    const iconStyle = {
      width: '20px',
      height: '20px',
      flexShrink: 0
    };

    switch (sliderId) {
      case 'energy':
        return (
          <img 
            src="/img/personality-energy.svg" 
            alt="Energy"
            style={iconStyle}
            className="slider-icon-svg"
            data-version="4.1"
          />
        );
      case 'data_depth':
        return (
          <img 
            src="/img/personality-data.svg" 
            alt="Data Depth"
            style={iconStyle}
            className="slider-icon-svg"
            data-version="4.1"
          />
        );
      case 'challenge':
        return (
          <img 
            src="/img/personality-challenge.svg" 
            alt="Challenge"
            style={iconStyle}
            className="slider-icon-svg"
            data-version="4.1"
          />
        );
      case 'humor':
        return (
          <img 
            src="/img/personality-humor.svg" 
            alt="Humor"
            style={iconStyle}
            className="slider-icon-svg"
            data-version="4.1"
          />
        );
      default:
        return (
          <svg 
            width="20" 
            height="20" 
            viewBox="0 0 24 24" 
            fill="none" 
            className="slider-icon-svg"
            data-version="4.0"
          >
            <circle cx="12" cy="12" r="3" stroke="#1F6C4F" fill="none" strokeWidth="2"/>
            <path d="M12 1v6m0 6v6m11-7h-6m-6 0H1" stroke="#1F6C4F" strokeWidth="2"/>
          </svg>
        );
    }
  };

  const getSliderExtremes = (sliderId: string): { low: string; high: string } => {
    const extremeMap: Record<string, { low: string; high: string }> = {
      energy: { low: 'Calm', high: 'Enthusiastic' },
      data_depth: { low: 'Keep It Simple', high: 'Data Deep Dives' },
      challenge: { low: 'Gentle Approach', high: 'Push Me Hard' },
      humor: { low: 'Professional Tone', high: 'Fun & Playful' }
    };
    return extremeMap[sliderId] || { low: 'Less', high: 'More' };
  };

  const getStaticLabels = (sliderId: string): Record<number, string> => {
    // Static labels showing what each value (1-5) means for this slider
    const labels: Record<string, Record<number, string>> = {
      energy: {
        1: 'Calm',
        2: 'Steady',
        3: 'Moderate',
        4: 'Energetic',
        5: 'Enthusiastic'
      },
      data_depth: {
        1: 'Simple',
        2: 'Basic',
        3: 'Moderate',
        4: 'Detailed',
        5: 'Deep Dives'
      },
      challenge: {
        1: 'Gentle',
        2: 'Supportive',
        3: 'Moderate',
        4: 'Challenging',
        5: 'Push Hard'
      },
      humor: {
        1: 'Professional',
        2: 'Serious',
        3: 'Moderate',
        4: 'Playful',
        5: 'Fun & Playful'
      }
    };

    return labels[sliderId] || { 1: 'Low', 2: 'Low-Mid', 3: 'Moderate', 4: 'Mid-High', 5: 'High' };
  };

  const getDynamicDescription = (sliderId: string, value: number): string => {
    // Use the value parameter (which comes from current state) directly
    const currentValue = Math.round(value);
    console.log(`🎛️ DESCRIPTION: slider=${sliderId}, value=${value}, rounded=${currentValue}`);
    
    const staticLabels = getStaticLabels(sliderId);
    return staticLabels[currentValue] || 'Moderate';
  };

  // Prevent rendering if no sliders
  if (!sliders || sliders.length === 0) {
    return null;
  }

  return (
    <div className="elite-personality-sliders">
      {/* Header */}
      <div className="sliders-header">
        <h3>Adjust Coach Cedar's Coaching Style</h3>
      </div>

      {/* Sliders Container */}
      <div className="sliders-container">
        {sliders.map((slider) => {
          // Use state value only (already converted to 1-5 scale on initialization)
          // Don't use slider.value prop here as it may be in backend scale
          let currentValue = values[slider.id];
          
          // If no state value exists, use default (shouldn't happen after useEffect, but safety check)
          if (currentValue === undefined || currentValue === null) {
            currentValue = 3; // Default to middle value
          }
          
          // Safety check: if value is still > 5, clamp it (shouldn't happen after useEffect)
          if (currentValue > 5) {
            currentValue = Math.min(5, convertFromBackendScale(currentValue));
          }
          
          // Clamp to valid 1-5 range (enforce slider min/max strictly)
          currentValue = Math.max(slider.min || 1, Math.min(slider.max || 5, Math.round(currentValue)));
          
          const percentage = ((currentValue - slider.min) / (slider.max - slider.min)) * 100;
          const extremes = getSliderExtremes(slider.id);

          return (
            <div key={slider.id} className="slider-row">
              {/* Header Row with Icon, Label, and Value */}
              <div className="slider-header-row">
                <div className="slider-icon">{getSliderIcon(slider.id)}</div>
                <span className="slider-label">{slider.label}</span>
                <div className="slider-value">
                  {currentValue}
                </div>
              </div>

              {/* Interactive Slider Control */}
              <div className="slider-control-row">
                <div
                  className="slider-track"
                  onClick={(e) => handleTrackClick(e, slider)}
                  style={{ '--slider-percentage': percentage } as React.CSSProperties}
                >
                  <div className="slider-track-bg" />
                  <div 
                    className="slider-track-fill" 
                    style={{ width: `${percentage}%` }}
                  />
                  <div
                    className="slider-thumb"
                    style={{ left: `calc(${percentage}% - var(--thumb-offset))` }}
                    onMouseDown={(e) => handleMouseDown(e, slider)}
                    onTouchStart={(e) => handleTouchStart(e, slider)}
                  />
                </div>
              </div>

              {/* Scale Row - 1-5 numbers only */}
              <div className="slider-scale-row">
                {Array.from({ length: 5 }, (_, i) => {
                  const scaleValue = i + 1;
                  const isActive = scaleValue === currentValue;
                  return (
                    <span 
                      key={i} 
                      className={`scale-mark ${isActive ? 'active' : ''}`}
                    >
                      {scaleValue}
                    </span>
                  );
                })}
              </div>

              {/* Static Labels Row - Shows what 1 and 5 mean (below slider) */}
              {(() => {
                const staticLabels = getStaticLabels(slider.id);
                return (
                  <div className="slider-static-labels-row">
                    <span className="static-label-1">{staticLabels[1]}</span>
                    <span className="static-label-5">{staticLabels[5]}</span>
                  </div>
                );
              })()}
            </div>
          );
        })}
      </div>

      {/* Submit Section - SIMPLE BUTTON APPROACH */}
      <div className="sliders-submit">
        <button
          type="button"
          onClick={!isSubmitting ? handleSubmit : undefined}
          disabled={isSubmitting}
          className="sliders-submit-button"
          style={{
            backgroundColor: isSubmitting ? '#94A3B8' : 'var(--cedar-primary, #1F6C4F)',
            color: '#FFFFFF',
            border: 'none',
            borderRadius: '10px',
            padding: '12px 32px',
            fontSize: '15px',
            fontWeight: '500',
            height: '42px',
            cursor: isSubmitting ? 'not-allowed' : 'pointer',
            minWidth: '200px',
            fontFamily: 'system-ui, -apple-system, sans-serif',
            transition: 'background-color 0.2s ease, transform 0.1s ease'
          }}
          data-text={isSubmitting ? 'Applying Changes...' : 'Save My Preferences'}
        >
          {isSubmitting ? 'Applying Changes...' : 'Save My Preferences'}
        </button>
      </div>
    </div>
  );
};

export default InlinePersonalitySliders; 